/**
 * @file WebsiteList.tsx
 * @description Component for displaying and managing a list of websites.
 */

import React, { useState, useEffect } from "react";
import { Website } from "../../types";
import { useWebsites } from "../../hooks/useWebsites";
import { useClients } from "../../hooks/useClients";
import WebsiteForm from "./WebsiteForm";
import Loading from "../common/Loading";

interface WebsiteListProps {
  autoSelectClientId?: string;
}

const WebsiteList: React.FC<WebsiteListProps> = ({ autoSelectClientId }) => {
  const { clients, loadClients } = useClients();
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const { websites, isLoading, createWebsite, updateWebsite, deleteWebsite } =
    useWebsites(selectedClientId || undefined);
  const [editingWebsite, setEditingWebsite] = useState<Website | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  // Auto-select client if there's exactly one client and none is selected
  useEffect(() => {
    if (clients.length === 1 && !selectedClientId) {
      setSelectedClientId(clients[0].id);
    }
  }, [clients, selectedClientId]);

  // Reload clients when a new one is created to ensure the list is up to date
  useEffect(() => {
    if (autoSelectClientId) {
      loadClients();
    }
  }, [autoSelectClientId, loadClients]);

  // Auto-select client when a new one is created, after clients list is updated
  useEffect(() => {
    if (
      autoSelectClientId &&
      autoSelectClientId !== selectedClientId &&
      clients.some((c) => c.id === autoSelectClientId)
    ) {
      setSelectedClientId(autoSelectClientId);
    }
  }, [autoSelectClientId, selectedClientId, clients]);

  const handleCreate = () => {
    if (!selectedClientId) {
      alert("Seleziona prima un cliente");
      return;
    }
    setEditingWebsite(null);
    setIsFormOpen(true);
  };

  const handleEdit = (website: Website) => {
    setEditingWebsite(website);
    setIsFormOpen(true);
  };

  const handleDelete = async (websiteId: string) => {
    if (
      window.confirm(
        "Sei sicuro di voler eliminare questo sito web? Questa azione non può essere annullata."
      )
    ) {
      try {
        await deleteWebsite(websiteId);
      } catch (error) {
        // Error handling is done in the hook
      }
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingWebsite(null);
  };

  const handleFormSubmit = async (websiteData: {
    clientId: string;
    website: string;
    contentType: string;
    wpUsername: string;
    wpAppPassword: string;
    wpPostType?: string;
    wpStatus?: string;
    wpCategories?: number[];
  }) => {
    try {
      if (editingWebsite) {
        await updateWebsite(editingWebsite.id, websiteData);
      } else {
        await createWebsite(websiteData);
      }
      handleFormClose();
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  if (isLoading && websites.length === 0 && !selectedClientId) {
    return <Loading />;
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-6">
        <h3 className="!text-3xl !font-medium text-primary">Siti Web</h3>
        <button
          onClick={handleCreate}
          className="px-6 text-base rounded-full btn btn-primary"
          disabled={!selectedClientId || isFormOpen}
        >
          + Nuovo sito web
        </button>
      </div>

      <div className="mb-6">
        <label htmlFor="client-filter" className="block mb-2 text-sm font-medium text-left">
          Filtra per cliente
        </label>
        <select
          id="client-filter"
          value={selectedClientId}
          onChange={(e) => setSelectedClientId(e.target.value)}
          className="w-full select select-bordered"
        >
          <option value="">Tutti i clienti</option>
          {clients.map((client) => (
            <option key={client.id} value={client.id}>
              {client.name}
            </option>
          ))}
        </select>
      </div>

      {/* Show form at top only when creating a new website */}
      {isFormOpen && !editingWebsite && (
        <WebsiteForm
          website={null}
          clients={clients}
          defaultClientId={selectedClientId || undefined}
          onSubmit={handleFormSubmit}
          onCancel={handleFormClose}
        />
      )}

      {!selectedClientId ? (
        <p className="text-gray">
          Seleziona un cliente per visualizzare i suoi siti web.
        </p>
      ) : websites.length === 0 ? (
        <p className="text-gray">
          Nessun sito web per questo cliente. Crea il primo!
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {websites.map((website) => (
            <div key={website.id}>
              {editingWebsite && editingWebsite.id === website.id ? (
                // In-place editing: show form instead of the row
                <WebsiteForm
                  website={editingWebsite}
                  clients={clients}
                  defaultClientId={selectedClientId || undefined}
                  onSubmit={handleFormSubmit}
                  onCancel={handleFormClose}
                  isInline={true}
                />
              ) : (
                // Normal row display
                <div className="flex flex-col gap-2 p-4 border rounded-lg border-gray-line bg-base-100">
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col text-left">
                      <span className="text-lg font-medium">{website.website}</span>
                      <span className="text-sm text-gray">
                        Tipo: {website.contentType}
                      </span>
                      <span className="text-sm text-gray">
                        Post Type: {website.wpPostType || "post"} | Status:{" "}
                        {website.wpStatus || "draft"}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(website)}
                        className="px-4 text-sm rounded-full btn btn-outline btn-primary"
                        disabled={isFormOpen}
                      >
                        Modifica
                      </button>
                      <button
                        onClick={() => handleDelete(website.id)}
                        className="px-4 text-sm rounded-full btn btn-outline btn-error"
                        disabled={isFormOpen}
                      >
                        Elimina
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WebsiteList;
